import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { HardHat, Plus, Trash2, Save, ArrowLeft, CheckCircle2, Circle } from 'lucide-react';

// Predefined craft list in alphabetical order
const PREDEFINED_CRAFTS = [
  { name: "Asbestos Workers", description: "Asbestos abatement" },
  { name: "Boilermakers", description: "Boiler fabrication and repair" },
  { name: "Bricklayers", description: "Brick and masonry work" },
  { name: "Carpenters", description: "Wood framing and finishing" },
  { name: "Cement Masons", description: "Concrete finishing and placement" },
  { name: "Drivers", description: "Commercial vehicle operation" },
  { name: "Electricians", description: "Electrical system installation and maintenance" },
  { name: "Glaziers", description: "Glass installation" },
  { name: "Insulators", description: "Thermal and acoustical insulation" },
  { name: "Iron Workers", description: "Structural steel and reinforcement" },
  { name: "Laborers", description: "General construction labor" },
  { name: "Metal Trades", description: "Metalworking and fabrication" },
  { name: "Millwrights", description: "Industrial machinery installation" },
  { name: "Operators", description: "Heavy equipment operation" },
  { name: "Painters", description: "Paint application and surface preparation" },
  { name: "Pile Drivers", description: "Foundation pile driving" },
  { name: "Pipefitters", description: "Pipe system installation" },
  { name: "Plasterers", description: "Plaster and stucco application" },
  { name: "Plumbers", description: "Plumbing system installation and repair" },
  { name: "Roofers", description: "Roofing installation and repair" },
  { name: "Sheet Metal Workers", description: "Sheet metal fabrication and installation" },
  { name: "Stonemasons", description: "Stone cutting and placement" },
  { name: "Tilesetters", description: "Tile and marble installation" },
  { name: "Warehouse Workers", description: "Warehouse operations and logistics" },
];

const CraftManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [crafts, setCrafts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCraft, setSelectedCraft] = useState(null);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [showAddCraftDialog, setShowAddCraftDialog] = useState(false);
  const [newCraft, setNewCraft] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [useCustomCraft, setUseCustomCraft] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (!userData || (userData.role !== 'company_admin' && userData.role !== 'super_admin')) {
      navigate('/login');
      return;
    }
    setUser(userData);
    loadCrafts();
    loadCourses();
  }, [navigate]);

  const loadCrafts = async () => {
    try {
      const response = await axiosInstance.get('/crafts');
      setCrafts(response.data);
    } catch (error) {
      console.error('Failed to load crafts:', error);
      toast.error('Failed to load crafts');
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axiosInstance.get('/admin/all-courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to load courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const handleCreateCraft = async (e) => {
    e.preventDefault();
    if (!newCraft.name.trim()) {
      toast.error('Craft name is required');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post('/crafts', newCraft);
      toast.success('Craft created successfully');
      setShowAddCraftDialog(false);
      setNewCraft({ name: '', description: '' });
      setUseCustomCraft(false);
      loadCrafts();
    } catch (error) {
      console.error('Failed to create craft:', error);
      toast.error(error.response?.data?.detail || 'Failed to create craft');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCraft = async (craftId, craftName) => {
    if (!window.confirm(`Are you sure you want to delete craft: ${craftName}?\n\nThis will not delete courses, but will remove this craft from all courses.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/crafts/${craftId}`);
      toast.success('Craft deleted successfully');
      if (selectedCraft?.id === craftId) {
        setSelectedCraft(null);
        setSelectedCourseIds([]);
      }
      loadCrafts();
    } catch (error) {
      console.error('Failed to delete craft:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete craft');
    }
  };

  const handleSelectCraft = (craft) => {
    setSelectedCraft(craft);
    // Find all courses that have this craft assigned
    const coursesWithCraft = courses.filter(course => 
      course.crafts && course.crafts.includes(craft.name)
    );
    setSelectedCourseIds(coursesWithCraft.map(c => c.id));
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCourseIds(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleSaveCraftCourses = async () => {
    if (!selectedCraft) {
      toast.error('Please select a craft first');
      return;
    }

    setLoading(true);
    try {
      // Update each course to add/remove this craft
      const updatePromises = courses.map(async (course) => {
        const shouldHaveCraft = selectedCourseIds.includes(course.id);
        const currentlyHasCraft = course.crafts && course.crafts.includes(selectedCraft.name);

        if (shouldHaveCraft !== currentlyHasCraft) {
          let updatedCrafts = course.crafts || [];
          
          if (shouldHaveCraft) {
            // Add craft
            updatedCrafts = [...updatedCrafts, selectedCraft.name];
          } else {
            // Remove craft
            updatedCrafts = updatedCrafts.filter(c => c !== selectedCraft.name);
          }

          return axiosInstance.patch(`/courses/${course.id}/crafts`, updatedCrafts);
        }
      });

      await Promise.all(updatePromises.filter(p => p !== undefined));
      toast.success(`Courses updated for ${selectedCraft.name}`);
      loadCourses(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to update courses:', error);
      toast.error('Failed to update courses');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/training')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Training
              </Button>
              <div className="flex items-center gap-2">
                <HardHat className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold">Craft Management</h1>
              </div>
            </div>
            <Button onClick={() => setShowAddCraftDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Craft
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Craft List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Crafts ({crafts.length})</CardTitle>
              <CardDescription>Select a craft to assign courses</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {crafts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No crafts yet</p>
                  <p className="text-sm">Click "Add Craft" to create one</p>
                </div>
              ) : (
                crafts.map(craft => (
                  <div
                    key={craft.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedCraft?.id === craft.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-400 hover:bg-slate-50'
                    }`}
                    onClick={() => handleSelectCraft(craft)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold">{craft.name}</h3>
                        {craft.description && (
                          <p className="text-xs text-slate-600 mt-1">{craft.description}</p>
                        )}
                        <Badge variant="outline" className="mt-2">
                          {courses.filter(c => c.crafts && c.crafts.includes(craft.name)).length} courses
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCraft(craft.id, craft.name);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Right Panel - Course Assignment */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>
                {selectedCraft ? `Assign Courses to ${selectedCraft.name}` : 'Select a Craft'}
              </CardTitle>
              <CardDescription>
                {selectedCraft 
                  ? `Select all courses that apply to ${selectedCraft.name} workers`
                  : 'Choose a craft from the left to manage its courses'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedCraft ? (
                <div className="text-center py-16 text-slate-500">
                  <HardHat className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p className="text-lg">Select a craft to begin</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm text-slate-600">
                      {selectedCourseIds.length} of {courses.length} courses selected
                    </p>
                    <Button onClick={handleSaveCraftCourses} disabled={loading}>
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-3 max-h-[600px] overflow-y-auto">
                    {courses.map(course => {
                      const isSelected = selectedCourseIds.includes(course.id);
                      return (
                        <div
                          key={course.id}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-green-500 bg-green-50'
                              : 'border-slate-200 hover:border-slate-400'
                          }`}
                          onClick={() => toggleCourseSelection(course.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              {isSelected ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                              ) : (
                                <Circle className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold">{course.title}</h4>
                              <p className="text-sm text-slate-600 mt-1">{course.description}</p>
                              <div className="flex gap-2 mt-2">
                                <Badge variant="secondary">{course.category}</Badge>
                                <Badge variant="outline">{course.duration} min</Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Craft Dialog */}
      <Dialog open={showAddCraftDialog} onOpenChange={setShowAddCraftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Craft</DialogTitle>
            <DialogDescription>Create a new craft/trade for your organization</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCraft}>
            <div className="space-y-4 py-4">
              {/* Super Admin: Option to use custom craft */}
              {user?.role === 'super_admin' && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="use-custom"
                    checked={useCustomCraft}
                    onChange={(e) => {
                      setUseCustomCraft(e.target.checked);
                      setNewCraft({ name: '', description: '' });
                    }}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="use-custom" className="cursor-pointer text-sm font-medium">
                    Add custom craft not in predefined list
                  </Label>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="craft-name">{useCustomCraft ? 'Custom Craft Name' : 'Select Craft'} *</Label>
                {useCustomCraft ? (
                  <Input
                    id="craft-name"
                    placeholder="Enter custom craft name"
                    value={newCraft.name}
                    onChange={(e) => setNewCraft({ ...newCraft, name: e.target.value })}
                    required
                  />
                ) : (
                  <Select
                    value={newCraft.name}
                    onValueChange={(value) => {
                      const selectedPredefined = PREDEFINED_CRAFTS.find(c => c.name === value);
                      setNewCraft({
                        name: value,
                        description: selectedPredefined?.description || ''
                      });
                    }}
                  >
                    <SelectTrigger id="craft-name">
                      <SelectValue placeholder="Choose a craft from the list" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {PREDEFINED_CRAFTS
                        .filter(craft => !crafts.some(existing => existing.name === craft.name))
                        .map((craft) => (
                          <SelectItem key={craft.name} value={craft.name}>
                            {craft.name}
                          </SelectItem>
                        ))}
                      {PREDEFINED_CRAFTS.filter(craft => !crafts.some(existing => existing.name === craft.name)).length === 0 && (
                        <div className="p-4 text-center text-sm text-slate-500">
                          All predefined crafts have been added
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-slate-500">
                  {useCustomCraft 
                    ? 'Enter a custom craft name for special cases'
                    : 'Select from the alphabetically sorted list to prevent misspellings'
                  }
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="craft-description">Description</Label>
                <Input
                  id="craft-description"
                  placeholder={useCustomCraft ? "Enter description" : "Description (auto-filled)"}
                  value={newCraft.description}
                  onChange={(e) => setNewCraft({ ...newCraft, description: e.target.value })}
                  className={useCustomCraft ? "" : "bg-slate-50"}
                />
                <p className="text-xs text-slate-500">
                  {useCustomCraft 
                    ? 'Provide a brief description of the custom craft'
                    : 'Description is auto-filled but can be customized'
                  }
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddCraftDialog(false);
                setNewCraft({ name: '', description: '' });
                setUseCustomCraft(false);
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !newCraft.name}>
                {loading ? 'Creating...' : 'Create Craft'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CraftManagement;
